'use client';
import CmsPageRenderer from '@/components/CmsPageRenderer';

/**
 * Public product documentation. Content lives in the `cms_page_docs`
 * Setting row — same multi-lang JSON shape as About/Impressum/AGB.
 * Super-admin edits at /super-admin/cms-pages → docs.
 *
 * The inline `fallbacks` below are the shipped default until a CMS override
 * exists. DE/EN/RU are authored; the other six languages fall back to DE
 * (CmsPageRenderer picks fallbacks[lang] ?? fallback).
 *
 * Auto-update rule: every batch that ships a feature MUST add or update a
 * section here (and in the CMS default) in the same commit.
 */

type Doc = { title: string; subtitle: string; sections: { id: string; title: string; body: string }[] };

const de: Doc = {
  title: 'Dokumentation',
  subtitle: 'Wie LOGIXA funktioniert — vom Kundenauftrag bis zur Zustellung, mit allen Modulen in einem Login.',
  sections: [
    {
      id: 'overview',
      title: 'Übersicht',
      body:
        'LOGIXA ist eine Multi-Tenant-Logistikplattform mit drei Hauptrollen: **Kunde** (Versender), **Spedition** (Organisation) und **Fahrer**. Die Plattform läuft als Web-Cockpit, iOS-App, Android-App und Telegram-Bot — alle sprechen mit derselben API.\n\nStatt fünf Einzeltools nebeneinander zu betreiben, deckt ein einziges System die komplette operative Kette ab: Auftrag, Disposition, GPS, Dokumente, Sicherheit, Kraftstoff, Abrechnung und Auszahlung. Jede Aktion landet im Audit-Log — wer, was, wann.',
    },
    {
      id: 'auftraege-disposition',
      title: 'Aufträge & KI-Disposition',
      body:
        'Aufträge entstehen manuell, per Excel-/CSV-Import, über die öffentliche API oder direkt aus dem B2C-Marktplatz. Die **KI-Disposition** schlägt die optimale Fahrer-Fahrzeug-Zuordnung vor und berücksichtigt dabei Lenkzeit-Restbudget, Überstunden und Fahrzeugverfügbarkeit.\n\n- **E-CMR-Frachtbrief** mit Barcode/QR und elektronischer Unterschrift\n- Lade- und Entladefotos automatisch an den Auftrag geheftet\n- Reklamationen mit Foto-Beweis und Eskalations-SLA\n- Status-Updates in Echtzeit aus der Fahrer-App',
    },
    {
      id: 'gps-tacho',
      title: 'GPS, Telematik & Tachograph',
      body:
        'Live-Position jedes LKW auf der Karte, Refresh alle 15 Sekunden — ganz ohne Drittanbieter-Tracker. Routenverlauf bis 90 Tage zurück, Geofence-Alarme und markierte Geschwindigkeitsverstöße.\n\n- **Tachograph & EU-Verordnung 561/2006**: Lenk- und Ruhezeiten in Echtzeit\n- Verstoß-Prognose, *bevor* der Verstoß entsteht\n- DDD-Datei-Import und Verstoß-Report\n- Öffentlicher Customer-Tracking-Link (ohne Login) für Endkunden',
    },
    {
      id: 'fahrer-dokumente',
      title: 'Fahrer, Dokumente & Fristen',
      body:
        'Komplette HR- und Asset-Stammdaten an einem Ort. Automatische Warnungen 30/14/7 Tage vor Ablauf von Führerschein, ADR, Modul 95, ärztlicher Untersuchung, TÜV und Versicherung.\n\n- Fahrer fotografiert Tankbeleg → **OCR** liest Betrag, Datum, Liter → Beleg wandert in die Buchhaltung\n- Volltextsuche über alle Dokumente einer Organisation\n- Verschlüsselte Speicherung (AES-256) im EU-Datenraum, Export als ZIP / DATEV-CSV',
    },
    {
      id: 'fahrsicherheit',
      title: 'Fahrsicherheit & Coaching',
      body:
        'Die Safety-Suite macht aus dem Smartphone des Fahrers ein vollständiges Sicherheitssystem — **ohne teure Hardware**.\n\n- **Safety-Score** je Fahrer, pro 100 km normiert und vergleichbar\n- **Auto-Coaching** in 9 Sprachen: konkrete, freundliche Hinweise statt Strafzettel\n- **Dashcam-Clips** und **Müdigkeitswarnung** direkt vom Telefon\n- **SOS** mit Vorfall-Puffer: bei Auslösung werden die ±5 Minuten rund um das Ereignis gesichert und an die Disposition gemeldet',
    },
    {
      id: 'kraftstoff',
      title: 'Kraftstoff-Intelligenz',
      body:
        'LOGIXA kennt die **Live-Dieselpreise** und sagt dem Fahrer, *wo* und *wie viel* er tanken soll, um auf der Route am günstigsten durchzukommen.\n\n- Echtzeit-Preisquellen für **Deutschland (Tankerkönig), Frankreich, Österreich und Spanien**\n- Tank-Empfehlung entlang der geplanten Route: Station, Menge, Ersparnis\n- Verbrauchsanalyse je Fahrzeug und Fahrer, Anbindung an die TCO-Rechnung\n- Eigener Tankerkönig-API-Schlüssel verschlüsselt in den Einstellungen hinterlegbar',
    },
    {
      id: 'frachtboerse-marktplatz',
      title: 'Frachtbörse & Marktplatz',
      body:
        'Leeren Laderaum füllen und freie Ladung finden — direkt im TMS, ohne zweite Software.\n\n- **Frachtbörse**: TIMOCOM und Trans.eu direkt integriert\n- **Freight-Allocation**: die Plattform schlägt passende Ladungen zu freien Fahrzeugen vor\n- **Lagerplätze (B2B)**: Lagerfläche und Rampen kurzfristig mieten oder vermieten\n- **B2C-Marktplatz**: Privat- und Geschäftskunden buchen Transporte direkt',
    },
    {
      id: 'routenplaner',
      title: 'KI-Routenplaner',
      body:
        'Die optimale LKW-Route berücksichtigt mehr als nur die kürzeste Strecke.\n\n- Berechnung von **Maut, Kraftstoff und CO₂** für jede Fahrt\n- **Gewinn-Rechner** je Tour: Erlös minus Maut, Diesel und Zeit\n- LKW-Profil (Höhe, Gewicht, ADR) fließt in die Streckenwahl ein\n- Direkte Übergabe an Auftrag und Disposition',
    },
    {
      id: 'abrechnung',
      title: 'Lohn, Abrechnung & Auszahlungen',
      body:
        'Automatische Lohnberechnung mit Überstunden, Zuschlägen und Multi-Währung — Monatsabschluss mit einem Klick.\n\n- Internes Guthaben, **SEPA** und **Stripe-Connect**-Direktzahlungen\n- T+1-Auszahlung an Selbstständige nach Tourabschluss\n- Buchungshistorie, Abrechnungen und Export für den Steuerberater',
    },
    {
      id: 'integrationen',
      title: 'Integrationen & API',
      body:
        'LOGIXA lässt sich an bestehende Systeme anbinden und über No-Code-Tools mit über 6000 Apps verbinden.\n\n- **REST-API** + **Webhooks** (HMAC-signiert) für ERP/Buchhaltung\n- **Zapier, n8n und Make**: Trigger bei Auftrag, SOS oder Zustellung\n- **KI-Import**: Excel/CSV hochladen — die KI erkennt und mappt die Spalten selbst\n- SDKs für **TypeScript** und **Python**, Sonderintegrationen (DATEV, SAP, TIMOCOM) auf Anfrage',
    },
    {
      id: 'sicherheit-dsgvo',
      title: 'Sicherheit & DSGVO',
      body:
        'Hosting in der EU (Frankfurt) unter MATTALNET GRUPP OÜ (Tallinn, EE).\n\n- **JWT**, bcrypt-12, **2FA** und Passkeys\n- Strikte Mandantentrennung — jede Anfrage ist auf die eigene Organisation gefiltert\n- Audit-Log auf jeder Aktion\n- DSGVO-Export und -Löschung per Knopfdruck',
    },
    {
      id: 'cta',
      title: 'Loslegen',
      body:
        'Am besten live im **Free-Plan** testen — keine Kreditkarte, kein Lock-in.\n\n[Unternehmen anlegen](/register) · [Preise vergleichen](/pricing) · [Demo vereinbaren](/kontakt)',
    },
  ],
};

const en: Doc = {
  title: 'Documentation',
  subtitle: 'How LOGIXA works — from customer order to delivery, with all modules in a single login.',
  sections: [
    {
      id: 'overview',
      title: 'Overview',
      body:
        'LOGIXA is a multi-tenant logistics platform with three main roles: **customer** (shipper), **carrier** (organisation) and **driver**. It runs as a web cockpit, iOS app, Android app and Telegram bot — all talking to the same API.\n\nInstead of running five separate tools side by side, one system covers the entire operational chain: order, dispatch, GPS, documents, safety, fuel, billing and payout. Every action lands in the audit log — who, what, when.',
    },
    {
      id: 'auftraege-disposition',
      title: 'Orders & AI Dispatch',
      body:
        'Orders are created manually, via Excel/CSV import, through the public API or straight from the B2C marketplace. **AI dispatch** proposes the best driver–vehicle match, factoring in remaining drive-time budget, overtime and vehicle availability.\n\n- **e-CMR waybill** with barcode/QR and electronic signature\n- Loading and unloading photos attached to the order automatically\n- Claims with photo proof and escalation SLA\n- Real-time status updates from the driver app',
    },
    {
      id: 'gps-tacho',
      title: 'GPS, Telematics & Tachograph',
      body:
        'Live position of every truck on the map, refreshed every 15 seconds — without any third-party tracker. Route history up to 90 days back, geofence alerts and flagged speeding.\n\n- **Tachograph & EU Regulation 561/2006**: drive and rest times in real time\n- Violation forecast *before* it happens\n- DDD file import and violation report\n- Public customer tracking link (no login) for end customers',
    },
    {
      id: 'fahrer-dokumente',
      title: 'Drivers, Documents & Deadlines',
      body:
        'Complete HR and asset master data in one place. Automatic warnings 30/14/7 days before expiry of licence, ADR, Module 95, medical, MOT and insurance.\n\n- Driver photographs a fuel receipt → **OCR** reads amount, date, litres → receipt flows into accounting\n- Full-text search across every document of an organisation\n- Encrypted storage (AES-256) in the EU data space, export as ZIP / DATEV CSV',
    },
    {
      id: 'fahrsicherheit',
      title: 'Driver Safety & Coaching',
      body:
        'The Safety Suite turns the driver’s smartphone into a full safety system — **with no expensive hardware**.\n\n- **Safety score** per driver, normalised per 100 km and comparable\n- **Auto-coaching** in 9 languages: concrete, friendly nudges instead of penalty notices\n- **Dashcam clips** and **fatigue warning** straight from the phone\n- **SOS** with incident buffer: on trigger, the ±5 minutes around the event are saved and reported to dispatch',
    },
    {
      id: 'kraftstoff',
      title: 'Fuel Intelligence',
      body:
        'LOGIXA knows the **live diesel prices** and tells the driver *where* and *how much* to refuel to cross the route at the lowest cost.\n\n- Real-time price sources for **Germany (Tankerkönig), France, Austria and Spain**\n- Refuel recommendation along the planned route: station, amount, saving\n- Consumption analysis per vehicle and driver, wired into the TCO calculation\n- Your own Tankerkönig API key can be stored encrypted in settings',
    },
    {
      id: 'frachtboerse-marktplatz',
      title: 'Freight Exchange & Marketplace',
      body:
        'Fill empty capacity and find free loads — directly in the TMS, without a second piece of software.\n\n- **Freight exchange**: TIMOCOM and Trans.eu integrated directly\n- **Freight allocation**: the platform suggests matching loads for free vehicles\n- **Warehouse slots (B2B)**: rent or let storage space and ramps short-term\n- **B2C marketplace**: private and business customers book transport directly',
    },
    {
      id: 'routenplaner',
      title: 'AI Route Planner',
      body:
        'The optimal truck route accounts for more than just the shortest distance.\n\n- Calculation of **toll, fuel and CO₂** for every trip\n- **Profit calculator** per tour: revenue minus toll, diesel and time\n- Truck profile (height, weight, ADR) feeds into route choice\n- Direct hand-off to order and dispatch',
    },
    {
      id: 'abrechnung',
      title: 'Payroll, Billing & Payouts',
      body:
        'Automatic payroll with overtime, bonuses and multi-currency — month-end close in one click.\n\n- Internal balance, **SEPA** and **Stripe Connect** direct payments\n- T+1 payout to the self-employed after tour completion\n- Booking history, statements and export for the tax advisor',
    },
    {
      id: 'integrationen',
      title: 'Integrations & API',
      body:
        'LOGIXA connects to existing systems and links to over 6000 apps via no-code tools.\n\n- **REST API** + **webhooks** (HMAC-signed) for ERP/accounting\n- **Zapier, n8n and Make**: triggers on order, SOS or delivery\n- **AI import**: upload Excel/CSV — the AI detects and maps the columns itself\n- SDKs for **TypeScript** and **Python**, custom integrations (DATEV, SAP, TIMOCOM) on request',
    },
    {
      id: 'sicherheit-dsgvo',
      title: 'Security & GDPR',
      body:
        'Hosting in the EU (Frankfurt) under MATTALNET GRUPP OÜ (Tallinn, EE).\n\n- **JWT**, bcrypt-12, **2FA** and passkeys\n- Strict tenant isolation — every request is scoped to its own organisation\n- Audit log on every action\n- GDPR export and deletion at the push of a button',
    },
    {
      id: 'cta',
      title: 'Get started',
      body:
        'Best tested live on the **Free plan** — no credit card, no lock-in.\n\n[Create company](/register) · [Compare pricing](/pricing) · [Book a demo](/kontakt)',
    },
  ],
};

const ru: Doc = {
  title: 'Документация',
  subtitle: 'Как работает LOGIXA — от заказа клиента до доставки, все модули под одним логином.',
  sections: [
    {
      id: 'overview',
      title: 'Обзор',
      body:
        'LOGIXA — мультитенантная логистическая платформа с тремя основными ролями: **клиент** (отправитель), **перевозчик** (организация) и **водитель**. Работает как веб-кокпит, приложение iOS, приложение Android и Telegram-бот — все они обращаются к одному API.\n\nВместо пяти отдельных инструментов одна система закрывает всю операционную цепочку: заказ, диспетчеризация, GPS, документы, безопасность, топливо, расчёты и выплаты. Каждое действие фиксируется в журнале аудита — кто, что, когда.',
    },
    {
      id: 'auftraege-disposition',
      title: 'Заказы и ИИ-диспетчеризация',
      body:
        'Заказы создаются вручную, импортом из Excel/CSV, через публичный API или прямо с B2C-маркетплейса. **ИИ-диспетчеризация** предлагает оптимальную связку «водитель—машина» с учётом остатка времени вождения, переработок и доступности техники.\n\n- **Электронная CMR** со штрих-/QR-кодом и электронной подписью\n- Фото погрузки и выгрузки автоматически прикрепляются к заказу\n- Рекламации с фотодоказательством и SLA эскалации\n- Обновление статусов в реальном времени из приложения водителя',
    },
    {
      id: 'gps-tacho',
      title: 'GPS, телематика и тахограф',
      body:
        'Живое положение каждого грузовика на карте, обновление каждые 15 секунд — без сторонних трекеров. История маршрута до 90 дней, геозоны и отмеченные превышения скорости.\n\n- **Тахограф и Регламент ЕС 561/2006**: режимы труда и отдыха в реальном времени\n- Прогноз нарушения *до* того, как оно произойдёт\n- Импорт DDD-файлов и отчёт о нарушениях\n- Публичная ссылка отслеживания для конечного клиента (без входа)',
    },
    {
      id: 'fahrer-dokumente',
      title: 'Водители, документы и сроки',
      body:
        'Полные HR- и asset-данные в одном месте. Автопредупреждения за 30/14/7 дней до истечения прав, ADR, Модуля 95, медосмотра, ТО и страховки.\n\n- Водитель фотографирует чек на топливо → **OCR** считывает сумму, дату, литры → чек уходит в бухгалтерию\n- Полнотекстовый поиск по всем документам организации\n- Шифрованное хранение (AES-256) в ЕС, экспорт в ZIP / DATEV-CSV',
    },
    {
      id: 'fahrsicherheit',
      title: 'Безопасность вождения и коучинг',
      body:
        'Safety-Suite превращает смартфон водителя в полноценную систему безопасности — **без дорогого оборудования**.\n\n- **Safety-Score** по каждому водителю, нормированный на 100 км и сравнимый\n- **Авто-коучинг** на 9 языках: конкретные дружелюбные подсказки вместо штрафов\n- **Клипы с видеорегистратора** и **предупреждение об усталости** прямо с телефона\n- **SOS** с буфером инцидента: при срабатывании сохраняются ±5 минут вокруг события и отправляются диспетчеру',
    },
    {
      id: 'kraftstoff',
      title: 'Топливный интеллект',
      body:
        'LOGIXA знает **актуальные цены на дизель** и подсказывает водителю, *где* и *сколько* заправить, чтобы пройти маршрут дешевле всего.\n\n- Источники цен в реальном времени для **Германии (Tankerkönig), Франции, Австрии и Испании**\n- Рекомендация по заправке вдоль маршрута: станция, объём, экономия\n- Анализ расхода по машине и водителю, привязка к расчёту TCO\n- Свой ключ Tankerkönig можно хранить в зашифрованном виде в настройках',
    },
    {
      id: 'frachtboerse-marktplatz',
      title: 'Биржа грузов и маркетплейс',
      body:
        'Заполнить пустой пробег и найти свободный груз — прямо в TMS, без второй программы.\n\n- **Биржа грузов**: TIMOCOM и Trans.eu интегрированы напрямую\n- **Freight-Allocation**: платформа подбирает грузы под свободные машины\n- **Складские места (B2B)**: краткосрочная аренда/сдача склада и рамп\n- **B2C-маркетплейс**: частные и бизнес-клиенты бронируют перевозки напрямую',
    },
    {
      id: 'routenplaner',
      title: 'ИИ-планировщик маршрутов',
      body:
        'Оптимальный маршрут грузовика учитывает не только кратчайшее расстояние.\n\n- Расчёт **платных дорог, топлива и CO₂** для каждой поездки\n- **Калькулятор прибыли** по рейсу: выручка минус платные дороги, дизель и время\n- Профиль грузовика (высота, вес, ADR) влияет на выбор маршрута\n- Прямая передача в заказ и диспетчеризацию',
    },
    {
      id: 'abrechnung',
      title: 'Зарплата, расчёты и выплаты',
      body:
        'Автоматический расчёт зарплаты с переработками, надбавками и мультивалютой — закрытие месяца в один клик.\n\n- Внутренний баланс, прямые платежи **SEPA** и **Stripe Connect**\n- Выплата T+1 самозанятым после завершения рейса\n- История операций, отчёты и экспорт для бухгалтера',
    },
    {
      id: 'integrationen',
      title: 'Интеграции и API',
      body:
        'LOGIXA подключается к существующим системам и связывается с более чем 6000 приложений через no-code инструменты.\n\n- **REST API** + **вебхуки** (с HMAC-подписью) для ERP/бухгалтерии\n- **Zapier, n8n и Make**: триггеры на заказ, SOS или доставку\n- **ИИ-импорт**: загрузите Excel/CSV — ИИ сам распознаёт и сопоставляет колонки\n- SDK для **TypeScript** и **Python**, спец-интеграции (DATEV, SAP, TIMOCOM) по запросу',
    },
    {
      id: 'sicherheit-dsgvo',
      title: 'Безопасность и GDPR',
      body:
        'Хостинг в ЕС (Франкфурт) под MATTALNET GRUPP OÜ (Таллин, EE).\n\n- **JWT**, bcrypt-12, **2FA** и passkeys\n- Строгая изоляция тенантов — каждый запрос ограничен своей организацией\n- Журнал аудита на каждое действие\n- Экспорт и удаление данных по GDPR одной кнопкой',
    },
    {
      id: 'cta',
      title: 'Начать',
      body:
        'Лучше всего протестировать вживую на **бесплатном тарифе** — без карты и без привязки.\n\n[Создать компанию](/register) · [Сравнить тарифы](/pricing) · [Запросить демо](/kontakt)',
    },
  ],
};

export default function DocsPage() {
  return (
    <CmsPageRenderer
      slug="docs"
      fallback={de}
      fallbacks={{ de, en, ru }}
    />
  );
}
